import { useCallback, useEffect, useState } from "react";
import { APP_CURRENCY, APP_LOCALE } from "../../../config/locale";
import { Modal } from "../../../components/Modal";
import {
  ApiError,
  createWorkAfterSales,
  deleteWorkAfterSales,
  getWorkAfterSales,
  updateWorkAfterSales,
  type WorkAfterSalesInterventionDto,
  type WorkAfterSalesInterventionsVm,
} from "../../../workAfterSales/workAfterSalesApi";

interface WorkAfterSalesTabProps {
  workId: number;
}

const currencyFormatter = new Intl.NumberFormat(APP_LOCALE, {
  style: "currency",
  currency: APP_CURRENCY,
});

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return currencyFormatter.format(0);
  }
  return currencyFormatter.format(value);
};

// Data-only → dd/mm/yyyy sem passar por Date (evita desvio de fuso horário em
// strings YYYY-MM-DD).
const formatDate = (value: string | null | undefined): string => {
  if (!value) return "—";
  const [y, m, d] = value.slice(0, 10).split("-");
  if (y && m && d) return `${d}/${m}/${y}`;
  return value;
};

const todayIso = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Parse tolerante (aceita vírgula ou ponto); vazio → null (campo opcional).
const parseOptionalNumber = (value: string): number | null => {
  const normalized = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

interface InterventionForm {
  description: string;
  interventionDate: string;
  responsible: string;
  estimatedValue: string;
  actualValue: string;
  notes: string;
}

type ModalState =
  | { kind: "create"; form: InterventionForm }
  | { kind: "edit"; intervention: WorkAfterSalesInterventionDto; form: InterventionForm }
  | null;

const inputClass =
  "px-3 py-2 border border-border rounded bg-background text-foreground text-sm";
const linkAction =
  "underline cursor-pointer transition-opacity hover:opacity-70 disabled:opacity-50 disabled:cursor-not-allowed";

const emptyForm = (): InterventionForm => ({
  description: "",
  interventionDate: todayIso(),
  responsible: "",
  estimatedValue: "",
  actualValue: "",
  notes: "",
});

const formFromDto = (
  intervention: WorkAfterSalesInterventionDto,
): InterventionForm => ({
  description: intervention.description ?? "",
  interventionDate: intervention.interventionDate?.slice(0, 10) ?? todayIso(),
  responsible: intervention.responsible ?? "",
  estimatedValue:
    intervention.estimatedValue === null ||
    intervention.estimatedValue === undefined
      ? ""
      : String(intervention.estimatedValue),
  actualValue:
    intervention.actualValue === null || intervention.actualValue === undefined
      ? ""
      : String(intervention.actualValue),
  notes: intervention.notes ?? "",
});

export function WorkAfterSalesTab({ workId }: WorkAfterSalesTabProps) {
  const [vm, setVm] = useState<WorkAfterSalesInterventionsVm | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const [modal, setModal] = useState<ModalState>(null);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        const data = await getWorkAfterSales(workId);
        setVm(data);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível carregar as intervenções pós-venda.",
        );
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [workId],
  );

  useEffect(() => {
    load();
  }, [load]);

  const interventions = vm?.interventions ?? [];

  // ---- Abertura de modais ---------------------------------------------------
  const openCreate = () => {
    setError(null);
    setModal({ kind: "create", form: emptyForm() });
  };

  const openEdit = (intervention: WorkAfterSalesInterventionDto) => {
    setError(null);
    setModal({ kind: "edit", intervention, form: formFromDto(intervention) });
  };

  const closeModal = () => setModal(null);

  const patchForm = (patch: Partial<InterventionForm>) =>
    setModal((prev) =>
      prev ? { ...prev, form: { ...prev.form, ...patch } } : prev,
    );

  // ---- Submissões -----------------------------------------------------------
  const handleSave = async () => {
    if (!modal || isSaving) return;
    const { form } = modal;
    if (!form.description.trim()) {
      setError("Descreva a intervenção.");
      return;
    }
    if (!form.interventionDate) {
      setError("Indique a data da intervenção.");
      return;
    }
    setIsSaving(true);
    setError(null);
    const payload = {
      description: form.description.trim(),
      interventionDate: form.interventionDate,
      responsible: form.responsible.trim() || null,
      estimatedValue: parseOptionalNumber(form.estimatedValue),
      actualValue: parseOptionalNumber(form.actualValue),
      notes: form.notes.trim() || null,
    };
    try {
      if (modal.kind === "create") {
        await createWorkAfterSales(workId, payload);
      } else {
        await updateWorkAfterSales(workId, modal.intervention.id, payload);
      }
      closeModal();
      await load(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Não foi possível guardar a intervenção.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (interventionId: number) => {
    if (deletingId !== null) return;
    setDeletingId(interventionId);
    setError(null);
    try {
      await deleteWorkAfterSales(workId, interventionId);
      setConfirmDeleteId(null);
      await load(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setConfirmDeleteId(null);
        await load(true);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível eliminar a intervenção.",
        );
      }
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      {/* Toolbar / resumo */}
      <div
        className="bg-card border border-border px-4 py-4 flex items-center justify-between gap-3 flex-wrap"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <div className="flex flex-col gap-1">
          <span className="text-secondary-text text-sm">
            {interventions.length} intervenç
            {interventions.length === 1 ? "ão" : "ões"} pós-venda
          </span>
          <span className="text-sm">
            <span className="text-secondary-text">
              Custo pós-venda imputado:{" "}
            </span>
            <span className="text-green-700 font-medium">
              {formatCurrency(vm?.actualTotal ?? 0)}
            </span>
            <span className="text-secondary-text">
              {"  ·  Estimado: "}
              {formatCurrency(vm?.estimatedTotal ?? 0)}
            </span>
          </span>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          + Nova intervenção
        </button>
      </div>

      {/* Tabela */}
      <div
        className="bg-card border border-border overflow-x-auto"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full" style={{ minWidth: 900 }}>
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3" style={{ width: 120 }}>
                Data
              </th>
              <th className="px-4 py-3">Descrição</th>
              <th className="px-4 py-3" style={{ width: 180 }}>
                Responsável
              </th>
              <th className="px-4 py-3 text-right" style={{ width: 140 }}>
                Valor estimado
              </th>
              <th className="px-4 py-3 text-right" style={{ width: 160 }}>
                Valor real
              </th>
              <th className="px-4 py-3" style={{ width: 160 }}>
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-center text-secondary-text"
                >
                  A carregar…
                </td>
              </tr>
            ) : interventions.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-center text-secondary-text"
                >
                  Sem intervenções pós-venda registadas.
                </td>
              </tr>
            ) : (
              interventions.map((intervention) => (
                <tr
                  key={intervention.id}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    {formatDate(intervention.interventionDate)}
                  </td>
                  <td className="px-4 py-3 break-words">
                    {intervention.description || "—"}
                  </td>
                  <td className="px-4 py-3 break-words">
                    {intervention.responsible || "—"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-secondary-text">
                    {intervention.estimatedValue === null ||
                    intervention.estimatedValue === undefined
                      ? "—"
                      : formatCurrency(intervention.estimatedValue)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {intervention.hasImputedCost ? (
                      <span className="inline-flex items-center gap-2 justify-end flex-wrap">
                        <span className="text-foreground font-medium">
                          {formatCurrency(intervention.actualValue)}
                        </span>
                        <span
                          className="inline-flex items-center px-2 py-0.5 text-xs bg-green-500/10 text-green-700"
                          style={{ borderRadius: "var(--radius)" }}
                          title="Valor imputado ao custo da obra"
                        >
                          imputado
                        </span>
                      </span>
                    ) : (
                      <span className="text-secondary-text">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-3 flex-wrap items-center">
                      <button
                        type="button"
                        className={`${linkAction} text-primary`}
                        onClick={() => openEdit(intervention)}
                      >
                        Editar
                      </button>
                      {confirmDeleteId === intervention.id ? (
                        <span className="inline-flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-secondary-text">
                            Apagar?
                          </span>
                          <button
                            type="button"
                            disabled={deletingId === intervention.id}
                            onClick={() => handleDelete(intervention.id)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-destructive/10 text-destructive cursor-pointer disabled:opacity-50"
                            style={{ borderRadius: "var(--radius)" }}
                          >
                            {deletingId === intervention.id
                              ? "A eliminar…"
                              : "Confirmar"}
                          </button>
                          <button
                            type="button"
                            disabled={deletingId === intervention.id}
                            onClick={() => setConfirmDeleteId(null)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border text-foreground hover:bg-accent/50 cursor-pointer disabled:opacity-50"
                            style={{ borderRadius: "var(--radius)" }}
                          >
                            Cancelar
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className={`${linkAction} text-destructive`}
                          onClick={() => setConfirmDeleteId(intervention.id)}
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal — Nova / Editar intervenção */}
      {modal && (
        <Modal
          title={
            modal.kind === "create"
              ? "Nova intervenção pós-venda"
              : "Editar intervenção pós-venda"
          }
          onClose={closeModal}
          maxWidthClass="max-w-2xl"
          footer={
            <>
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 border border-border"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving
                  ? "A guardar…"
                  : modal.kind === "create"
                    ? "Criar intervenção"
                    : "Guardar"}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Descrição *
              <textarea
                rows={2}
                value={modal.form.description}
                onChange={(event) =>
                  patchForm({ description: event.target.value })
                }
                className={inputClass}
              />
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Data *
                <input
                  type="date"
                  value={modal.form.interventionDate}
                  onChange={(event) =>
                    patchForm({ interventionDate: event.target.value })
                  }
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Responsável
                <input
                  type="text"
                  value={modal.form.responsible}
                  onChange={(event) =>
                    patchForm({ responsible: event.target.value })
                  }
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Valor estimado
                <input
                  type="text"
                  inputMode="decimal"
                  value={modal.form.estimatedValue}
                  onChange={(event) =>
                    patchForm({ estimatedValue: event.target.value })
                  }
                  placeholder="0,00"
                  className={inputClass}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Valor real
                <input
                  type="text"
                  inputMode="decimal"
                  value={modal.form.actualValue}
                  onChange={(event) =>
                    patchForm({ actualValue: event.target.value })
                  }
                  placeholder="0,00"
                  className={inputClass}
                />
              </label>
            </div>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Notas
              <textarea
                rows={2}
                value={modal.form.notes}
                onChange={(event) => patchForm({ notes: event.target.value })}
                className={inputClass}
              />
            </label>
            <div
              className="bg-muted border border-border px-3 py-2 text-sm text-secondary-text"
              style={{ borderRadius: "var(--radius)" }}
            >
              O valor real é imputado ao custo da obra (visão pós-venda); o
              estimado é só indicativo.
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
